/**
 * AppRail — the 9-item primary navigation rail used by every app workspace.
 *
 * Items mirror the design bundle's `NAV_PRIMARY` constant (.agents/design/project/data.jsx:58-68)
 * verbatim — order, labels, icons. The single source of truth lives here so the
 * rail stays consistent across pages and never drifts back into per-page stubs.
 *
 * Each module's workspace passes `active` as the matching `NavId`. Items whose
 * routes haven't landed yet stay `disabled`; flip the `disabled` flag in the
 * NAV table when wiring is done.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Rail } from './Rail';
import { AppTopBar } from './AppTopBar';
import { NavDrawer } from './NavDrawer';
import { UserMenuBrand } from './AppShell';
import type { IconName } from './Icon';
import { useBreakpoint } from '@/lib/useBreakpoint';
import { useModuleAccess } from '@/lib/permissions';
import { APP_VERSION } from '@/modules/dashboard/novinky';

export type NavId =
  | 'prehled'
  | 'ukoly'
  | 'kontakty'
  | 'vozidla'
  | 'ojeta'
  | 'leasing'
  | 'zakaznici'
  | 'servis'
  | 'posta'
  | 'kalendar'
  | 'pujcovna'
  | 'management'
  | 'platforma';

export interface NavEntry {
  id: NavId;
  label: string;
  icon: IconName;
  to?: string;
  disabled?: boolean;
}

/** Single source of truth for primary nav — consumed by the rail (desktop +
 * tablet) and `NavDrawer` (phone) so the two can never drift. */
export const NAV_PRIMARY: NavEntry[] = [
  { id: 'prehled', label: 'Přehled', icon: 'home', to: '/' },
  { id: 'ukoly', label: 'Úkoly', icon: 'list', to: '/ukoly' },
  { id: 'kontakty', label: 'Kontakty', icon: 'users', to: '/kontakty' },
  { id: 'vozidla', label: 'Vozidla', icon: 'car', to: '/vozidla' },
  { id: 'ojeta', label: 'Ojetá vozidla', icon: 'key', to: '/ojeta-vozidla' },
  { id: 'leasing', label: 'Leasing', icon: 'wallet', to: '/leasing' },
  { id: 'zakaznici', label: 'Zákazníci', icon: 'user-check', to: '/zakaznici' },
  { id: 'servis', label: 'Servis', icon: 'wrench', to: '/servis/planovani' },
  { id: 'posta', label: 'Pošta', icon: 'mail', to: '/posta' },
  { id: 'kalendar', label: 'Kalendář', icon: 'calendar', to: '/kalendar' },
  { id: 'pujcovna', label: 'Půjčovna', icon: 'clock', to: '/pujcovna' },
  { id: 'management', label: 'Správa firmy', icon: 'chart', to: '/management' },
  // Platform-admin only — every company, every user (never a company module).
  { id: 'platforma', label: 'Platforma', icon: 'settings', to: '/platforma' },
];

interface AppRailProps {
  active: NavId;
  /** Fallback letter for the brand mark when membership data hasn't loaded
   * yet (e.g. anonymous health route). Once `/v1/auth/me` arrives, the brand
   * shows the active company's initials regardless of this value. */
  brand?: string;
  version?: string;
}

export function AppRail({
  active,
  brand = 'A',
  version = APP_VERSION,
}: AppRailProps) {
  const navigate = useNavigate();
  const { access, resolved, canAccess, homePath } = useModuleAccess();
  // Phone nav (Phase 14.1): AppRail owns the top bar + drawer so every
  // workspace gets phone navigation without touching its Shell call site.
  // The two surfaces are JS-swapped (not CSS-hidden) so only one
  // `user-menu-trigger` / "Hlavní navigace" landmark exists at a time.
  const { isPhone } = useBreakpoint();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Guard: every workspace renders AppRail with its module's `active` id, so
  // this is the single chokepoint that bounces a user out of a module they
  // can't access (e.g. a servis-read-only user who deep-links to /vozidla,
  // or lands on the dashboard). Wait for membership to resolve, and never
  // redirect on an unknown/empty access set (avoids loops + premature bounces).
  useEffect(() => {
    if (!resolved || access.size === 0) return;
    // Management is exempt from the bounce: its page renders a graceful 403
    // "požádejte správce" state for non-admins / no-module companies (and the
    // rail item is already hidden for them), so a deep-link/bookmark lands on
    // that explanation rather than a silent redirect.
    if (active === 'management' || active === 'platforma') return;
    if (!canAccess(active)) {
      navigate({ to: homePath, replace: true });
    }
  }, [resolved, access, canAccess, active, homePath, navigate]);

  // Until membership resolves, render the full rail (no flash of a stripped
  // nav); once resolved, show only the modules the user may reach.
  const items = resolved
    ? NAV_PRIMARY.filter((item) => access.has(item.id))
    : NAV_PRIMARY;

  const activeLabel = NAV_PRIMARY.find((item) => item.id === active)?.label ?? '';

  if (isPhone) {
    return (
      <>
        <AppTopBar title={activeLabel} onMenuClick={() => setDrawerOpen(true)} brand={brand} />
        <NavDrawer
          active={active}
          open={drawerOpen}
          onOpenChange={setDrawerOpen}
          version={version}
        />
      </>
    );
  }

  return (
    <Rail
      brand={<UserMenuBrand fallbackLetter={brand} />}
      unstyledBrand
      version={version}
    >
      {items.map((item) =>
        item.to && !item.disabled ? (
          <Rail.LinkItem
            key={item.id}
            icon={item.icon}
            label={item.label}
            active={item.id === active}
            to={item.to}
          />
        ) : (
          <Rail.Item
            key={item.id}
            icon={item.icon}
            label={item.label}
            active={item.id === active}
            disabled={item.disabled}
          />
        ),
      )}
    </Rail>
  );
}
